import React from 'react';
import {CheckIcon, EqualsIcon, PauseIcon} from '@heroicons/react/24/outline';
import {ColorPicker, Divider} from 'components';
import {useEditorStyle} from 'hooks';
import {classNames, formatColorResult} from 'utils';

import EditorInspector, {type IChildrenProps} from '../../inspectors/Editor.Inspector';
import EditorInspectorPadding from '../../inspectors/Editor.Inspector.Padding';

interface IEditorInspectorContainerProps {}

const EditorInspectorContainer = ({}: IEditorInspectorContainerProps) => {
  return (
    <EditorInspector title="Container" defaultDisplay="flex">
      {props => {
        return (
          <>
            <ContainerLayout {...props} />
            <Divider />
            <EditorInspectorPadding {...props} />
          </>
        );
      }}
    </EditorInspector>
  );
};

export default React.memo(EditorInspectorContainer);

interface IEditProps extends IChildrenProps {}

const ContainerLayout = ({style, setProp}: IEditProps) => {
  const directions = [
    {flexDirection: 'column', text: 'Rows', icon: EqualsIcon},
    {flexDirection: 'row', text: 'Columns', icon: PauseIcon},
  ];
  const justify = [
    {justifyContent: 'flex-start', text: 'Start'},
    {justifyContent: 'flex-end', text: 'End'},
    {justifyContent: 'center', text: 'Center'},
    {justifyContent: 'space-between', text: 'Between'},
  ];
  const align = [
    {alignItems: 'flex-start', text: 'Start'},
    {alignItems: 'flex-end', text: 'End'},
    {alignItems: 'center', text: 'Center'},
  ];
  const {updateStyle} = useEditorStyle({setProp});
  return (
    <div className="pb-2">
      <h4 className="font-medium text-sm text-slate-800 dark:text-white pb-6">Layout</h4>
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between">
          <p className="text-sm">Background</p>
          <ColorPicker
            alignment="right"
            color={style?.backgroundColor ?? 'white'}
            onPicked={color => {
              updateStyle('backgroundColor', formatColorResult(color));
            }}
          />
        </div>

        <div className="flex items-center justify-between">
          <p className="text-sm">Display</p>
          <div className="flex items-center text-slate-800 dark:text-darkText border divide-x rounded-xs">
            {directions.map(direction => {
              return (
                <button
                  type="button"
                  key={direction.flexDirection}
                  className="cursor-pointer p-0.5 hover:brightness-105 dark:hover:brightness-120"
                  onClick={() => {
                    updateStyle('flexDirection', direction.flexDirection);
                  }}>
                  <direction.icon
                    className={classNames('size-6 cursor-pointer', style?.flexDirection === direction.flexDirection && 'text-primary')}
                  />
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4">
          <div className="flex flex-col gap-3">
            <p className="text-sm">Justify Content</p>
            <div className="flex flex-col gap-2 text-slate-800 dark:text-darkText">
              {justify.map(j => {
                const selected = style?.justifyContent === j.justifyContent;
                return (
                  <button
                    type="button"
                    key={j.justifyContent}
                    className="flex items-center gap-3 cursor-pointer hover:brightness-105 dark:hover:brightness-120"
                    onClick={() => {
                      updateStyle('justifyContent', j.justifyContent);
                    }}>
                    <div className={classNames('flex justify-center items-center size-4 rounded-full', selected ? 'bg-primary' : 'bg-slate-300')}>
                      {selected && <CheckIcon className="size-3 text-white" />}
                    </div>
                    <p className="text-sm">{j.text}</p>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <p className="text-sm">Align Items</p>
            <div className="flex flex-col gap-2 text-slate-800 dark:text-darkText">
              {align.map(a => {
                const selected = style?.alignItems === a.alignItems;
                return (
                  <button
                    type="button"
                    key={a.alignItems}
                    className="flex items-center gap-3 cursor-pointer hover:brightness-105 dark:hover:brightness-120"
                    onClick={() => {
                      updateStyle('alignItems', a.alignItems);
                    }}>
                    <div className={classNames('flex justify-center items-center size-4 rounded-full', selected ? 'bg-primary' : 'bg-slate-300')}>
                      {selected && <CheckIcon className="size-3 text-white" />}
                    </div>
                    <p className="text-sm">{a.text}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
